import { fetcher, useApplicantAPI } from "@certn/api";
import { components } from "@certn/api/generated-schemas/applicant-portal";
import {
  showToast,
  ButtonGroup,
  Link,
  PrimaryButton,
  Stack,
  Text,
  GhostButton,
} from "@certn/cascadia";
import {
  ContractScroll,
  ESIGN_DOCUMENT_DEFAULT_HEIGHT,
  Loader,
  Space,
  Checkbox,
  ESignDocument,
} from "@certn/cascadia-react";
import {
  CascadiaControlProps,
  CascadiaJsonFormContext,
  withCascadiaControlProps,
} from "@certn/forms";
import { css } from "@emotion/css";
import * as Sentry from "@sentry/react";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  useContext,
  useEffect,
  useMemo,
  useState,
  useReducer,
  useCallback,
  useRef,
} from "react";
import { useTranslation, Trans } from "react-i18next";

import { ConsentRequiredModal } from "./ConsentRequiredModal";
import { ConsentsSectionMessage } from "./ConsentsSectionMessage";
import { ListViewConsents } from "./ListViewConsents";
import { initialState, manageConsent } from "./utils";
import { isSuperDocConsent } from "./consentListItem";
import { useApplication } from "../../ApplicationContext";
import { useApplicationFormContext } from "../../ApplicationFormContext";
import { ConsentLayoutContext } from "../../layouts/ConsentLayoutContext";
import { useESignSubmit } from "../../esign/useESignSubmit";
import { createESignDownloadHandler } from "../../esign/createESignDownloadHandler";
import { CLICKWRAP_ACCESS_ID } from "../constants";

// Consent type from generated schema (includes esign fields)
type ConsentWithESign = components["schemas"]["ApplicantAgreementTemplate"];

export function ConsentControl(props: CascadiaControlProps) {
  const { handleChange, path } = props;

  const { t } = useTranslation();
  const { submit, setValues, values } = useContext(CascadiaJsonFormContext);
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const { label, subLabel } = useContext(ConsentLayoutContext);

  const syncConsentAcceptedToForm = useCallback(() => {
    const next = { ...valuesRef.current, [path]: true };
    handleChange(path, true);
    setValues(next);
  }, [handleChange, path, setValues]);

  const { navigateToSection, prevSection } = useApplication();
  const { caseId } = useApplicationFormContext();

  const { enableSuperdocApplicantAgreementTemplates } = useFlags();

  const getConsentId = useCallback(
    (consent: ConsentWithESign) =>
      isSuperDocConsent(consent, enableSuperdocApplicantAgreementTemplates)
        ? consent.id ?? consent.external_id
        : consent.external_id,
    [enableSuperdocApplicantAgreementTemplates]
  );

  const {
    data,
    isLoading: consentsLoading,
    error,
  } = useApplicantAPI(
    `/api/internal/applicant/case/${caseId}/agreement-templates/` as "/api/internal/applicant/case/{id}/agreement-templates/"
  );

  const consents = useMemo(
    () => data?.consents as ConsentWithESign[] | undefined,
    [data?.consents]
  );

  const displayAdditionalCheckbox = useMemo(
    () => data?.display_additional_checkbox,
    [data?.display_additional_checkbox]
  );

  const localStorageKey = `${caseId}-optional-consumer-report-checkbox-value`;

  const [isSigningConsents, setIsSigningConsents] = useState(false);
  const [showConsentRequiredModal, setShowConsentRequiredModal] =
    useState(false);

  // required to prevent rerendering ContractScroll when data changes
  const scrollViewConsents = useMemo(
    () => consents?.filter((consent) => consent.display_in_scroll_view) ?? [],
    [consents]
  );

  const scrollViewConsentIDs = useMemo(
    () => scrollViewConsents.map(getConsentId),
    [scrollViewConsents, getConsentId]
  );

  const [state, dispatch] = useReducer(manageConsent, initialState);

  const pactSafeConsentIDs = useMemo(
    () =>
      scrollViewConsents
        .filter(
          (c) =>
            !isSuperDocConsent(c, enableSuperdocApplicantAgreementTemplates)
        )
        .map((c) => c.external_id),
    [scrollViewConsents, enableSuperdocApplicantAgreementTemplates]
  );

  const pactSafeIndex = useMemo(() => {
    const consent = scrollViewConsents[state.scrollViewConsentIndex];
    if (
      !consent ||
      isSuperDocConsent(consent, enableSuperdocApplicantAgreementTemplates)
    )
      return 0;
    return pactSafeConsentIDs.indexOf(consent.external_id);
  }, [
    pactSafeConsentIDs,
    scrollViewConsents,
    state.scrollViewConsentIndex,
    enableSuperdocApplicantAgreementTemplates,
  ]);

  const [renderConsentsInList, setRenderConsentsInList] = useState(false);

  const [
    optionalConsumerReportCheckboxValue,
    setOptionalConsumerReportCheckboxValue,
  ] = useState<boolean>(
    localStorage.getItem(localStorageKey) === "true" || false
  );

  const didHydrateCompletedState = useRef(false);

  // If data is true then we mark all consents as signed (scroll-view IDs plus list view).
  useEffect(() => {
    if (didHydrateCompletedState.current) {
      return;
    }
    if (!props.data || consentsLoading || !consents) {
      return;
    }

    didHydrateCompletedState.current = true;
    dispatch({ type: "SET_COMPLETED_STATE", scrollViewConsentIDs });
  }, [props.data, scrollViewConsentIDs, consents, consentsLoading]);

  // If there are no scroll view contracts to display then set renderConsentsInList to True
  useEffect(() => {
    if (
      consents &&
      !consentsLoading &&
      consents.length > 0 &&
      scrollViewConsents.length === 0
    ) {
      setRenderConsentsInList(true);
    }
  }, [
    consents,
    consentsLoading,
    handleChange,
    path,
    scrollViewConsents.length,
  ]);

  // If there are no consents to be rendered then set the path to true
  // this is so the user can move straight through the section
  useEffect(() => {
    if (!consentsLoading && consents && consents.length < 1) {
      handleChange(path, true);
    }
  }, [consents, consentsLoading, handleChange, path]);

  // Monitor changes to the consents list and use that to mark the section as complete
  useEffect(() => {
    if (consents && consents.length > 1) {
      // Boolean: all the consents recived from the BE need to rendered in scroll views
      const noListViewConsentsToSign = consents?.every(
        (consent) => consent.display_in_scroll_view
      );

      // Boolean: have all the scroll view consents been signed
      const allScrollViewConsentsSigned = state.signedScrollViewConsents.every(
        (id) => scrollViewConsentIDs.includes(id)
      );

      // Boolean: true when all scroll view consents are signed and
      // either all list view consents are signed or there are no list view consents to sign
      const haveAllConsentsBeenSigned =
        allScrollViewConsentsSigned &&
        (state.signedListViewConsents || noListViewConsentsToSign);

      handleChange(path, haveAllConsentsBeenSigned);
    }
  }, [
    consents,
    handleChange,
    path,
    scrollViewConsentIDs,
    state.signedListViewConsents,
    state.signedScrollViewConsents,
  ]);

  const handleConsentsInSignedScrollViewConsents = () => {
    const { external_id } = scrollViewConsents[state.scrollViewConsentIndex];
    const isAlreadySigned =
      state.signedScrollViewConsents.includes(external_id);

    dispatch({
      type: isAlreadySigned ? "UNSIGN_CONSENT" : "SIGN_CONSENT",
      external_id,
    });
  };

  const currentConsent = scrollViewConsents[state.scrollViewConsentIndex];

  // throwErrors keeps SuperDoc interactive after a 400 (e.g. invalid PNG) so
  // the applicant can redraw and POST again without a full page reload.
  const { submitSignature } = useESignSubmit(caseId, {
    throwErrors: true,
    onSuccess: () => {
      if (!currentConsent) return;
      dispatch({
        type: "SIGN_CONSENT",
        external_id: getConsentId(currentConsent),
      });
    },
    onFailure: (e) => {
      Sentry.captureException(e);
      console.error("Error signing the document", e);
    },
  });

  const signAgreements = useCallback(async () => {
    try {
      await Promise.all(
        consents?.map(async (consent) => {
          if (
            isSuperDocConsent(
              consent,
              enableSuperdocApplicantAgreementTemplates
            )
          ) {
            return;
          }
          return await fetcher.submit(
            `/api/internal/applicant/case/${caseId}/sign-agreement/`,
            {
              external_id: consent.external_id,
              render_data: consent.render_data,
            }
          );
        }) || []
      );
      syncConsentAcceptedToForm();
    } catch (e) {
      Sentry.captureException(e);
      console.error("Error signing the documents", e);
      showToast(t("Something went wrong, try again"), {
        type: "error",
      });
      return false;
    }
    return true;
  }, [
    caseId,
    consents,
    enableSuperdocApplicantAgreementTemplates,
    syncConsentAcceptedToForm,
    t,
  ]);

  const handleNextConsent = useCallback(() => {
    const nextConsent = scrollViewConsents?.[state.scrollViewConsentIndex + 1];
    if (!nextConsent) {
      if (consents && consents.length > state.signedScrollViewConsents.length) {
        setRenderConsentsInList(true);
      }
    } else {
      dispatch({
        type: "INCREMENT_CONSENT",
        step: "next",
      });
    }
  }, [
    consents,
    scrollViewConsents,
    state.scrollViewConsentIndex,
    state.signedScrollViewConsents.length,
  ]);

  const handleNext = useCallback(async () => {
    const hasConsentsToSign = consents && consents.length > 0;
    const activeConsent = !renderConsentsInList ? currentConsent : undefined;
    const hasSignedActiveConsent =
      activeConsent &&
      state.signedScrollViewConsents.includes(getConsentId(activeConsent));

    if (
      (hasConsentsToSign && activeConsent && !hasSignedActiveConsent) ||
      (hasConsentsToSign && !activeConsent && !props.data)
    ) {
      setShowConsentRequiredModal(true);
      return;
    }

    if (consents) {
      if (consents.length < 1) {
        // If there are no consents to sign then submit the form
        syncConsentAcceptedToForm();
        submit();
      } else {
        try {
          setIsSigningConsents(true);

          if (
            activeConsent &&
            activeConsent !== consents[consents.length - 1]
          ) {
            // Fetch the next consent if there is one
            handleNextConsent();
          } else {
            const agreementsSigned = await signAgreements();
            if (agreementsSigned) {
              localStorage.setItem(
                localStorageKey,
                optionalConsumerReportCheckboxValue.toString()
              );
              submit();
            }
          }
        } catch (e) {
          Sentry.captureException(e);
          console.error("Error signing the documents", e);
          showToast(t("Something went wrong, try again"), {
            type: "error",
          });
          return;
        } finally {
          setIsSigningConsents(false);
        }
      }
    }
  }, [
    consents,
    currentConsent,
    getConsentId,
    handleNextConsent,
    props,
    renderConsentsInList,
    signAgreements,
    state.signedScrollViewConsents,
    submit,
    syncConsentAcceptedToForm,
    t,
    localStorageKey,
    optionalConsumerReportCheckboxValue,
  ]);

  const handleBack = useCallback(() => {
    // Not working when everything is in scroll view
    if (renderConsentsInList && state.signedScrollViewConsents.length > 0) {
      if (state.signedScrollViewConsents.length > 1) {
        dispatch({
          type: "INCREMENT_CONSENT",
          step: "previous",
        });
      }
      setRenderConsentsInList(false);
    }

    if (prevSection) {
      if (
        !consents ||
        (consents && consents?.length < 1) ||
        (!renderConsentsInList &&
          state.scrollViewConsentIndex <= scrollViewConsents.length - 1) ||
        (renderConsentsInList && scrollViewConsents.length === 0)
      ) {
        navigateToSection(prevSection);
      }
    }
  }, [
    consents,
    navigateToSection,
    prevSection,
    renderConsentsInList,
    scrollViewConsents.length,
    state.scrollViewConsentIndex,
    state.signedScrollViewConsents.length,
  ]);

  if (consentsLoading) {
    return <Loader height="500px" />;
  }

  return (
    <Stack gap="space-4">
      <div className={styles.heading}>
        <Text as="h1" style="form/page-title" testID="consent-title">
          {!renderConsentsInList && currentConsent
            ? currentConsent.template_name
            : label}
        </Text>

        <Text
          style="form/body-primary"
          color="text-weak"
          testID="consent-sub-title"
        >
          {subLabel}
        </Text>
      </div>

      <ConsentsSectionMessage
        error={error}
        consents={consents}
        allowZeroConsents
      />

      {consents &&
        consents.length > 0 &&
        (renderConsentsInList ? (
          <ListViewConsents
            consents={consents.filter(
              (consent) => !consent.display_in_scroll_view
            )}
            isSigned={state.signedListViewConsents}
            onChange={() =>
              dispatch({
                type: "TOGGLE_LIST_SIGN",
                value: !state.signedListViewConsents,
              })
            }
          />
        ) : (
          currentConsent &&
          (isSuperDocConsent(
            currentConsent,
            enableSuperdocApplicantAgreementTemplates
          ) ? (
            <ESignDocument
              key={`esign-${getConsentId(currentConsent)}`}
              style={{
                maxWidth: "560px",
                height: ESIGN_DOCUMENT_DEFAULT_HEIGHT,
              }}
              data={currentConsent.esign}
              onSubmit={(d) => submitSignature(d, currentConsent.id ?? "")}
              onDownload={createESignDownloadHandler(caseId, currentConsent.id)}
              downloadFilename={currentConsent.template_name}
            />
          ) : (
            <ContractScroll
              key={`contract-${currentConsent.external_id}`}
              accessID={CLICKWRAP_ACCESS_ID}
              signerID={caseId}
              index={pactSafeIndex}
              contractIDs={pactSafeConsentIDs}
              isSigned={state.signedScrollViewConsents.includes(
                currentConsent.external_id
              )}
              onChange={handleConsentsInSignedScrollViewConsents}
              renderData={currentConsent.render_data}
              label={t(
                "I acknowledge that I have received, read and understood this statement"
              )}
            />
          ))
        ))}

      {displayAdditionalCheckbox && (
        <Checkbox
          testID="display-california-disclosure-checkbox"
          onChange={(isSelected) => {
            setOptionalConsumerReportCheckboxValue(isSelected);
          }}
          isSelected={optionalConsumerReportCheckboxValue}
        >
          {t(
            "I would like to receive a free copy of my consumer report (or investigative consumer report, as applicable) by email"
          )}
        </Checkbox>
      )}

      <div className={styles.actionBar}>
        <ButtonGroup>
          {prevSection && (
            <GhostButton
              leftIcon="chevron_left"
              onPress={handleBack}
              testID="section-back-button"
            >
              {t("Back")}
            </GhostButton>
          )}

          <Space />

          <PrimaryButton
            onPress={handleNext}
            isLoading={isSigningConsents}
            testID="section-next-step-button"
          >
            {t("Next step")}
          </PrimaryButton>
        </ButtonGroup>
      </div>

      <ConsentRequiredModal
        show={showConsentRequiredModal}
        onClose={() => setShowConsentRequiredModal(false)}
        alternativeDialogContent={{
          header: t("Acknowledgement required"),
          content: (
            <>
              <Trans>
                You must review, and acknowledge receipt of, the notices and
                disclosures provided to you in connection with your check before
                you can move forward.
              </Trans>
              <br />
              <br />
              <Trans>
                If you have any questions regarding these documents, please
                contact us at{" "}
                <Link
                  testID="consent-required"
                  linkStyle="monochrome"
                  href="mailto:support@certn.co"
                >
                  support@certn.co
                </Link>
                . If you do not wish to complete your check please close this
                window.
              </Trans>
            </>
          ),
        }}
      />
    </Stack>
  );
}

const styles = {
  heading: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
  actionBar: css({
    paddingTop: `calc(${"var(--space-4)"} - 1px)`,
    paddingBottom: "var(--space-4)",
    marginTop: "var(--space-5)",
    borderTop: "1px solid var(--border-base)",
  }),
};

ConsentControl.displayName = "ConsentControl";
export const ConsentControlRenderer = withCascadiaControlProps(ConsentControl);
